import { useSelector } from 'react-redux';
import Breadcrumbs from '../../components/BreadCrumbs';
import { useEffect, useState, ReactNode } from 'react';
import CommanModal from '../../components/CommanModal';
import { logBrazeCustomEvent } from '../../config/braze';

const renderParagraphs = (text: string | undefined): ReactNode => {
  if (!text) return null;
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((para, idx) => (
    <p
      key={idx}
      className="text-[#1F1F1F] text-[17px] font-normal leading-6 mt-5 first:mt-0"
    >
      {renderBoldText(para)}
    </p>
  ));
};

const renderBoldText = (text: string): ReactNode => {
  if (!text) return null;
  const parts = text.split(/<b>(.*?)<\/b>/);
  return parts.map((part, idx) => {
    if (idx % 2 === 1) {
      return <b key={idx}>{part}</b>;
    }
    return part;
  });
};

const PreAuthorizedDebit = () => {
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const { entries, assets } = contentfulData;
  const { rb_links, rb_navigation, rb_preAuthDebit } = entries;

  const breadcrumbs = [
    { name: rb_navigation?.moreLogoTitle, href: '/' },
    {
      name: rb_navigation?.moreMenu?.pre_autorised_debit,
      href: '/pre-authorized-debit',
    },
  ];

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailToCopy, setEmailToCopy] = useState('');
  const [isEmailCopied, setIsEmailCopied] = useState(false);

  useEffect(() => {
    logBrazeCustomEvent('viewed_pre_authorized_debit_enrolment');
  }, []);

  const handleDownloadClick = () => {
    window.open(
      rb_links?.rbLinksCollection?.more_pre_auth_debit_form,
      '_blank'
    );
  };

  const handleDownloadKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleDownloadClick();
    }
  };

  const handleEmailClick = (email: string) => {
    setTimeout(() => {
      {
        setEmailToCopy(email);
        setShowEmailModal(true);
      }
    }, 500);
  };

  return (
    <main role="main" className="bg-bg-body py-4 md:px-8 px-4">
      <nav aria-label="Breadcrumb">
        <Breadcrumbs items={breadcrumbs} />
      </nav>

      <section
        className="w-full rounded-[15px] bg-white min-h-[70vh] flex-1 flex-col overflow-y-scroll scrollbar-hide md:p-6 p-2"
        aria-labelledby="pre-auth-title"
      >
        <header className="flex flex-row justify-between items-center relative">
          <h1
            id="pre-auth-title"
            className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] md:pb-7 pb-3"
          >
            {rb_preAuthDebit?.preAuthDebitDescription?.title}
          </h1>

          <div
            role="button"
            tabIndex={0}
            aria-label={`Download ${rb_preAuthDebit?.preAuthDebitDescription?.downloadCta} form`}
            className="cursor-pointer flex items-center gap-2.5 border border-solid border-[#DA291C] text-[#DA291C] p-2.5 text-base font-semibold bg-[#FFF]"
            onClick={handleDownloadClick}
            onKeyDown={handleDownloadKeyDown}
          >
            <p className="hidden md:block text-[#DA291C]">
              {rb_preAuthDebit?.preAuthDebitDescription?.downloadCta}
            </p>
            <img
              src={assets.statement_download_web_icon}
              className="w-6 h-6"
              alt="Download form icon"
            />
          </div>
        </header>

        <article className="w-full md:rounded-2xl md:border md:border-light-gray-border mt-4 md:p-8">
          {renderParagraphs(
            rb_preAuthDebit?.preAuthDebitDescription?.description
          )}

          {renderParagraphs(
            rb_preAuthDebit?.preAuthDebitDescription?.description_level1
          )}

          <p className="text-[#1F1F1F] text-[17px] font-normal leading-6 mt-3">
            {rb_preAuthDebit?.preAuthDebitDescription?.description_level2}
          </p>

          <p className="text-[#1F1F1F] text-[17px] font-normal leading-6 mt-4">
            {renderBoldText(rb_preAuthDebit?.preAuthDebitDescription?.email)}
          </p>

          <button
            role="button"
            tabIndex={0}
            onClick={() =>
              handleEmailClick(rb_preAuthDebit?.preAuthDebitDescription?.mailId)
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleEmailClick(
                  rb_preAuthDebit?.preAuthDebitDescription?.mailId
                );
              }
            }}
            className="text-[#DA291C] text-[17px] font-normal leading-6 cursor-pointer underline outline-none focus:ring-2 focus:ring-[#DA291C] mt-2"
            aria-label={`Send email to ${rb_preAuthDebit?.preAuthDebitDescription?.mailId}`}
          >
            {renderBoldText(rb_preAuthDebit?.preAuthDebitDescription?.mailId)}
          </button>

          <div className="mt-4" aria-label="Postal address">
            <div className="text-[#1F1F1F] text-[17px] font-normal leading-6">
              {renderParagraphs(
                rb_preAuthDebit?.preAuthDebitDescription?.postalAddress?.or
              )}
            </div>
            <p className="text-[#1F1F1F] text-[17px] font-normal leading-6 mt-2 mb-2">
              {renderBoldText(
                rb_preAuthDebit?.preAuthDebitDescription?.postalAddress?.mail
              )}
            </p>
          </div>

          <address className="not-italic">
            <p className="text-[#1F1F1F] text-[17px] font-normal leading-5">
              {rb_preAuthDebit?.preAuthDebitDescription?.postalAddress?.bank}
            </p>
            <p className="text-[#1F1F1F] text-[17px] font-normal leading-5">
              {rb_preAuthDebit?.preAuthDebitDescription?.postalAddress?.line1}
            </p>
            <p className="text-[#1F1F1F] text-[17px] font-normal leading-5">
              {rb_preAuthDebit?.preAuthDebitDescription?.postalAddress?.line2}
            </p>
            <p className="text-[#1F1F1F] text-[17px] font-normal leading-5">
              {rb_preAuthDebit?.preAuthDebitDescription?.postalAddress?.city}
            </p>
            <p className="text-[#1F1F1F] text-[17px] font-normal leading-5">
              {rb_preAuthDebit?.preAuthDebitDescription?.postalAddress?.code}
            </p>
          </address>

          <p className="text-[#1F1F1F] text-[17px] font-normal leading-6 mt-5">
            {rb_preAuthDebit?.preAuthDebitDescription?.description_level3}
          </p>

          <div aria-label="External link to Payments Canada">
            <p className="text-[#1F1F1F] text-[17px] font-normal leading-6 inline">
              {rb_preAuthDebit?.preAuthDebitDescription?.description_level4}
            </p>
            <a
              href={
                rb_preAuthDebit?.preAuthDebitDescription?.url
                  ? rb_preAuthDebit.preAuthDebitDescription.url.startsWith(
                      'http'
                    )
                    ? rb_preAuthDebit.preAuthDebitDescription.url
                    : `https://${rb_preAuthDebit.preAuthDebitDescription.url}`
                  : 'https://www.payments.ca'
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#DA291C] text-[17px] font-normal leading-6 cursor-pointer underline inline"
            >
              {rb_preAuthDebit?.preAuthDebitDescription?.url}
            </a>
            <p className="text-[#1F1F1F] text-[17px] font-normal leading-6 inline">
              {rb_preAuthDebit?.preAuthDebitDescription?.rule}
            </p>
          </div>
        </article>

        {showEmailModal && (
          <CommanModal
            isOpen={showEmailModal}
            jsxContent={
              <>
                <div className="mt-[20px] text-lg font-semibold text-center">
                  {rb_preAuthDebit?.preAuthDebitDescription?.errModalTitle}
                </div>
                <div className="mt-[20px]">
                  {rb_preAuthDebit?.preAuthDebitDescription?.errModalDesc}
                </div>
              </>
            }
            onClose={() => {
              setShowEmailModal(false);
            }}
            buttonText={
              rb_preAuthDebit?.preAuthDebitDescription?.emailErrorCancelCta
            }
            cardStatus={true}
            closeIcon={false}
            extraButtonText={
              rb_preAuthDebit?.preAuthDebitDescription?.emailErrorCopyCta
            }
            onExtraButtonClick={() => {
              // navigator.clipboard.writeText(emailToCopy).then(() => {
              //   alert(
              //     rb_preAuthDebit?.preAuthDebitDescription?.copiedDescription.replace(
              //       '%s',
              //       emailToCopy
              //     )
              //   );
              setShowEmailModal(false);
              setIsEmailCopied(true);
              // });
            }}
          />
        )}

        {isEmailCopied && (
          <CommanModal
            isOpen={isEmailCopied}
            jsxContent={
              <div className="mt-[20px] text-center">
                {rb_preAuthDebit?.preAuthDebitDescription?.copiedDescription.replace(
                  '%s',
                  emailToCopy
                )}
              </div>
            }
            onClose={() => {
              navigator.clipboard.writeText(emailToCopy).then(() => {
                setIsEmailCopied(false);
                setEmailToCopy(emailToCopy);
              });
            }}
            buttonText={rb_preAuthDebit?.preAuthDebitDescription?.okCta}
            cardStatus={true}
            closeIcon={false}
          />
        )}
      </section>
    </main>
  );
};

export default PreAuthorizedDebit;
